import * as React from "react";

/**
 * Native React form state for the appointment modal — `useReducer` underneath,
 * no third-party form library involved. Returns a small explicit API instead
 * of mimicking another library's shape.
 *
 * Mirrors formik's `enableReinitialize: true` behavior: when the caller hands
 * the hook a new `initialValues` *reference*, state resets. The caller is
 * responsible for memoizing `initialValues` over its real dependencies (the
 * appointment-modal memoizes over `[appointment, timezone, isBookit]`).
 */

export type FormStateApi<V extends Record<string, unknown>> = {
  values: V;
  touched: Partial<Record<keyof V & string, boolean>>;
  errors: Partial<Record<keyof V & string, string>>;
  setFieldValue: <K extends keyof V & string>(name: K, value: V[K]) => void;
  setValues: (next: V | ((prev: V) => V)) => void;
  markTouched: (name: keyof V & string) => void;
  setErrors: (errors: Partial<Record<keyof V & string, string>>) => void;
  resetWithInitial: (next: V) => void;
};

type State<V> = {
  values: V;
  touched: Partial<Record<keyof V & string, boolean>>;
  errors: Partial<Record<keyof V & string, string>>;
};

type Action<V> =
  | { type: "setFieldValue"; name: keyof V & string; value: unknown }
  | { type: "setValues"; values: V }
  | { type: "markTouched"; name: keyof V & string }
  | { type: "setErrors"; errors: Partial<Record<keyof V & string, string>> }
  | { type: "resetWithInitial"; values: V };

function reducer<V extends Record<string, unknown>>(state: State<V>, action: Action<V>): State<V> {
  switch (action.type) {
    case "setFieldValue":
      return {
        ...state,
        values: { ...state.values, [action.name]: action.value },
      };
    case "setValues":
      return { ...state, values: action.values };
    case "markTouched":
      return {
        ...state,
        touched: { ...state.touched, [action.name]: true },
      };
    case "setErrors":
      return { ...state, errors: action.errors };
    case "resetWithInitial":
      return { values: action.values, touched: {}, errors: {} };
    default:
      return state;
  }
}

export function useFormState<V extends Record<string, unknown>>(
  initialValues: V
): FormStateApi<V> {
  const [state, dispatch] = React.useReducer(reducer<V>, undefined, () => ({
    values: initialValues,
    touched: {},
    errors: {},
  }));

  // Mirror state into a ref so updater-style setValues can read fresh values
  // synchronously, without depending on a closure that may be stale.
  const stateRef = React.useRef(state);
  stateRef.current = state;

  // Reset when the caller passes a new `initialValues` reference (formik's
  // `enableReinitialize: true` analog).
  const prevInitialRef = React.useRef(initialValues);
  React.useEffect(() => {
    if (prevInitialRef.current === initialValues) return;
    prevInitialRef.current = initialValues;
    dispatch({ type: "resetWithInitial", values: initialValues });
  }, [initialValues]);

  const setFieldValue = React.useCallback<FormStateApi<V>["setFieldValue"]>(
    (name, value) => {
      dispatch({ type: "setFieldValue", name, value });
    },
    []
  );

  const setValues = React.useCallback<FormStateApi<V>["setValues"]>((next) => {
    const resolved =
      typeof next === "function"
        ? (next as (prev: V) => V)(stateRef.current.values)
        : next;
    dispatch({ type: "setValues", values: resolved });
  }, []);

  const markTouched = React.useCallback<FormStateApi<V>["markTouched"]>(
    (name) => {
      dispatch({ type: "markTouched", name });
    },
    []
  );

  const setErrors = React.useCallback<FormStateApi<V>["setErrors"]>(
    (errors) => {
      dispatch({ type: "setErrors", errors });
    },
    []
  );

  const resetWithInitial = React.useCallback<FormStateApi<V>["resetWithInitial"]>(
    (next) => {
      dispatch({ type: "resetWithInitial", values: next });
    },
    []
  );

  return {
    values: state.values,
    touched: state.touched,
    errors: state.errors,
    setFieldValue,
    setValues,
    markTouched,
    setErrors,
    resetWithInitial,
  };
}
